// =============================================================================
// SHARE SERVICE - Social media sharing utilities
// =============================================================================

/**
 * Supported sharing platforms
 */
export type SharePlatform = 'twitter' | 'facebook' | 'telegram' | 'threads' | 'clipboard' | 'native';

/**
 * Share content type based on game outcome
 */
export type ShareType = 'victory' | 'stalemate' | 'defeat' | 'gameOver';

/**
 * Base URL for the game
 */
const getBaseUrl = () => window.location.origin;

// =============================================================================
// PLATFORM-SPECIFIC SHARING
// =============================================================================

/**
 * Share to Twitter/X
 */
export function shareToTwitter(text: string, url?: string): void {
    const tweetText = url ? `${text}\n\n${url}` : text;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
}

/**
 * Share to Facebook
 */
export function shareToFacebook(url: string, quote?: string): void {
    const shareUrl = quote
        ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(quote)}`
        : `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
}

/**
 * Share to Telegram
 */
export function shareToTelegram(text: string, url?: string): void {
    const shareText = url ? `${text}\n\n${url}` : text;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url || getBaseUrl())}&text=${encodeURIComponent(shareText)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
}

/**
 * Share to Threads
 */
export function shareToThreads(text: string): void {
    // Threads uses web intent with text parameter
    const shareUrl = `https://www.threads.net/intent/post?text=${encodeURIComponent(text)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
}

// =============================================================================
// CLIPBOARD
// =============================================================================

/**
 * Copy text to clipboard with fallback for older browsers
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }

        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
    } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        return false;
    }
}

// =============================================================================
// WEB SHARE API (NATIVE MOBILE SHARING)
// =============================================================================

/**
 * Check if native Web Share API is available
 */
export function canUseNativeShare(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Share using native Web Share API (mobile)
 */
export async function nativeShare(title: string, text: string, url?: string): Promise<boolean> {
    if (!canUseNativeShare()) {
        return false;
    }

    try {
        await navigator.share({
            title,
            text,
            url: url || getBaseUrl(),
        });
        return true;
    } catch (err) {
        // User cancelled or error
        if ((err as Error).name !== 'AbortError') {
            console.error('Native share failed:', err);
        }
        return false;
    }
}

// =============================================================================
// MESSAGE TEMPLATES
// =============================================================================

interface GameStats {
    score: number;
    decisionNumber?: number;
}

/**
 * Generate share message based on game outcome
 */
export function generateShareMessage(shareType: ShareType, stats: GameStats): string {
    const url = getBaseUrl();

    switch (shareType) {
        case 'victory':
            return `🎯 Я досяг СТРАТЕГІЧНОЇ ПЕРЕВАГИ як Головнокомандувач ЗСУ!

Загальний рейтинг: ${Math.round(stats.score)}%
Пройдено 20 критичних рішень

Чи зможеш ти краще?
${url}

#Головнокомандувач #ЗСУ #Україна`;

        case 'stalemate':
            return `⚔️ Важка кампанія, але Україна тримається!

Мій рейтинг: ${Math.round(stats.score)}%
Кожне рішення має наслідки.

Прийми командування:
${url}

#Головнокомандувач #ЗСУ`;

        case 'defeat':
            return `⚠️ Операційний провал командування...

Мій рейтинг: ${Math.round(stats.score)}%
Чи зможеш ти уникнути моїх помилок?

${url}

#Головнокомандувач`;

        case 'gameOver':
            return `💀 Критична помилка командування!

Провал на рішенні #${stats.decisionNumber || '?'}
Чи зможеш ти краще?

${url}

#Головнокомандувач`;

        default:
            return `Зіграй у "Головнокомандувач" — текстовий симулятор рішень та наслідків.\n\n${url}`;
    }
}

/**
 * Determine share type based on score
 */
export function getShareType(score: number, isGameOver = false): ShareType {
    if (isGameOver) return 'gameOver';
    if (score >= 75) return 'victory';
    if (score >= 50) return 'stalemate';
    return 'defeat';
}
